import { Tooltip } from '@pantry/ui'
import React from 'react'
import {
  RECOVERY_TEMPLATES,
  type RecoveryTemplateKey,
} from '../../../../../../../../utils/cartRecovery'
import { DEFAULT_EMAIL_BRAND } from '../../../../../../../../utils/recoveryCampaignBlocks'
import { C } from '../../../shared/theme'
import { RecoverySection, choiceStyle, fieldLabel } from './recoveryStyles'

const OPTIONS: Array<{
  key: RecoveryTemplateKey
  name: string
  desc: string
  popular?: boolean
}> = [
  ...RECOVERY_TEMPLATES.map((t) => ({
    key: t.key,
    name: t.name,
    desc: t.desc,
    popular: t.badge === 'popular',
  })),
  { key: 'blank', name: 'Blank', desc: 'Start from a blank template' },
]

// "Choose a starting template" — a compact take on the Campaign Builder's
// Abandoned Cart template step: the three starters + Blank as one row of
// name-only tiles (the description shows in a tooltip). Picking one reseeds the
// subject, preview text, message and offer (see `buildTemplateMessage`) and
// is sent as the campaign's template. Below it, the email's brand colour:
// the store's by default, overridable for this email.
export const RecoveryTemplatePicker: React.FC<{
  value: RecoveryTemplateKey
  onChange: (next: RecoveryTemplateKey) => void
  // Effective colour (override or store brand); '' = none known.
  brandColor: string
  isCustomBrand: boolean
  onBrandColorChange: (next: string) => void
  onBrandColorReset: () => void
}> = ({
  value,
  onChange,
  brandColor,
  isCustomBrand,
  onBrandColorChange,
  onBrandColorReset,
}) => (
  <RecoverySection title="Choose a starting template">
    <div
      role="radiogroup"
      aria-label="Templates"
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
        gap: 6,
        marginTop: 4,
      }}
    >
      {OPTIONS.map((t) => {
        const selected = value === t.key
        return (
          <Tooltip key={t.key} width="100%" label={t.desc}>
            <button
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(t.key)}
              style={{
                ...choiceStyle(selected),
                padding: '8px 4px',
                whiteSpace: 'nowrap',
                fontSize: 12,
                lineHeight: 1.3,
                width: '100%',
                minWidth: 0,
                position: 'relative',
              }}
            >
              {t.popular ? (
                <span
                  style={{
                    position: 'absolute',
                    top: -7,
                    right: -4,
                    padding: '0 4px',
                    borderRadius: 3,
                    fontSize: 8.5,
                    fontWeight: 700,
                    lineHeight: '12px',
                    background: '#FFF7ED',
                    color: '#EA580C',
                    border: '1px solid #FED7AA',
                  }}
                >
                  🔥 Popular
                </span>
              ) : null}
              {t.name}
            </button>
          </Tooltip>
        )
      })}
    </div>
    <div
      style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}
    >
      <label
        style={{ ...fieldLabel, margin: 0 }}
        htmlFor="recovery-brand-color"
      >
        Brand colour
      </label>
      <input
        id="recovery-brand-color"
        type="color"
        value={brandColor || DEFAULT_EMAIL_BRAND}
        onChange={(e) => onBrandColorChange(e.target.value)}
        style={{
          width: 32,
          height: 24,
          padding: 0,
          border: `1px solid ${C.gray[200]}`,
          borderRadius: 4,
          background: 'none',
          cursor: 'pointer',
        }}
      />
      <span
        style={{
          fontSize: 12,
          color: C.gray[500],
          fontFamily: 'monospace',
        }}
      >
        {(brandColor || DEFAULT_EMAIL_BRAND).toUpperCase()}
      </span>
      {isCustomBrand ? (
        <button
          type="button"
          onClick={onBrandColorReset}
          style={{
            border: 'none',
            background: 'none',
            padding: 0,
            fontSize: 12,
            color: C.blue[700],
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Use store colour
        </button>
      ) : (
        <span style={{ fontSize: 12, color: C.gray[400] }}>Store colour</span>
      )}
    </div>
  </RecoverySection>
)
